import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  ClipboardList,
  Search,
  User,
  Users,
  Calendar,
  Clock,
  Wrench,
  Filter,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

const PER_PAGE = 50;

/**
 * The log stores who acted as an email, since that survives a rename and is
 * unambiguous. A reader wants the person, so show the name when we have one
 * and keep the address on hover. Falls back to the local part of the address
 * ("elizabeth" from elizabeth@...) for anyone without an account, which still
 * beats a full address in a narrow column.
 */
function parseBookedBy(scheduledBy, staffNames = {}) {
  if (!scheduledBy) return { label: '—', type: 'unknown', email: null };
  if (scheduledBy === 'booking_page') return { label: 'Customer', type: 'customer', email: null };
  if (scheduledBy === 'manual') return { label: 'Manual', type: 'system', email: null };
  if (scheduledBy === 'zuper_sync') return { label: 'Zuper', type: 'system', email: null };
  for (const prefix of ['staff_booking:', 'staff:']) {
    if (scheduledBy.startsWith(prefix)) {
      const email = scheduledBy.slice(prefix.length);
      const name = staffNames[email.toLowerCase()] || email.split('@')[0];
      return { label: name, type: 'staff', email };
    }
  }
  return { label: scheduledBy, type: 'unknown', email: null };
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function BookingLogs() {
  const { selectedLocationId } = useLocationContext();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [bookedBy, setBookedBy] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [copiedWo, setCopiedWo] = useState(null);

  // Debounce search input
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(window._bookingSearchTimer);
    window._bookingSearchTimer = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(0);
    }, 350);
  };

  const queryParams = useMemo(() => {
    const p = { bookedBy, page, perPage: PER_PAGE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [debouncedSearch, bookedBy, from, to, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['booking-logs', queryParams, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/admin/booking-logs', { params: queryParams });
      return data;
    },
    keepPreviousData: true,
  });

  const logs = data?.logs || [];
  const activity = data?.activity || [];
  const staffNames = data?.staffNames || {};
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setBookedBy('all');
    setFrom('');
    setTo('');
    setPage(0);
  };

  const hasFilters = debouncedSearch || bookedBy !== 'all' || from || to;

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-brand-light/60 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center shadow-sm">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-brand-dark">Booking Logs</h1>
                <p className="text-xs text-brand-neutral">All bookings made through the system</p>
              </div>
            </div>
            <div className="text-xs text-brand-neutral font-medium">
              {total} {total === 1 ? 'booking' : 'bookings'}
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4">

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-brand-light shadow-sm p-4">
            <div className="flex flex-wrap gap-3 items-end">

              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="text-[11px] font-semibold text-brand-neutral uppercase tracking-wider block mb-1.5">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral/60" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="Customer, asset, work order..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-brand-light rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                  />
                </div>
              </div>

              {/* Booked By */}
              <div className="min-w-[160px]">
                <label className="text-[11px] font-semibold text-brand-neutral uppercase tracking-wider block mb-1.5">Booked By</label>
                <select
                  value={bookedBy}
                  onChange={e => { setBookedBy(e.target.value); setPage(0); }}
                  className="w-full px-3 py-2 text-sm border border-brand-light rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary bg-white"
                >
                  <option value="all">All</option>
                  <option value="customer">Customer (self-booked)</option>
                  <option value="staff">Staff (Book for Customer)</option>
                  <option value="system">System / Manual</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="text-[11px] font-semibold text-brand-neutral uppercase tracking-wider block mb-1.5">From</label>
                <input
                  type="date"
                  value={from}
                  onChange={e => { setFrom(e.target.value); setPage(0); }}
                  className="px-3 py-2 text-sm border border-brand-light rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="text-[11px] font-semibold text-brand-neutral uppercase tracking-wider block mb-1.5">To</label>
                <input
                  type="date"
                  value={to}
                  onChange={e => { setTo(e.target.value); setPage(0); }}
                  className="px-3 py-2 text-sm border border-brand-light rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </div>

              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-brand-neutral hover:text-red-600 border border-brand-light hover:border-red-200 rounded-xl transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-brand-light shadow-sm overflow-hidden">

            {isLoading ? (
              <div className="p-16 text-center">
                <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-brand-neutral">Loading bookings...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-16 text-center">
                <ClipboardList className="w-10 h-10 text-brand-neutral/30 mx-auto mb-3" />
                <p className="text-sm font-medium text-brand-dark">No bookings found</p>
                <p className="text-xs text-brand-neutral mt-1">
                  {hasFilters ? 'Try adjusting your filters' : 'Bookings will appear here once customers or staff make appointments'}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-brand-light/60 bg-gradient-to-r from-brand-light/30 to-white">
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Work Order</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Customer</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Asset</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Service Date</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Slot</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Mechanic</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Booked By</th>
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-brand-neutral uppercase tracking-wider">Booked At</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-light/40">
                      {logs.map((log) => {
                        const booked = parseBookedBy(log.scheduled_by, staffNames);
                        return (
                          <tr key={log.id} className="hover:bg-brand-light/20 transition-colors">
                            <td className="px-4 py-3">
                              <div className="group/wo inline-flex items-center gap-1.5">
                                <span className="font-mono text-xs text-brand-neutral">{log.work_order_number || '—'}</span>
                                {log.work_order_number && (
                                  <span className="inline-flex items-center gap-0.5 opacity-0 group-hover/wo:opacity-100 transition-opacity">
                                    {log.job_uid && (
                                      <button
                                        onClick={() => window.open(`https://web.zuperpro.com/jobs/${log.job_uid}/details`, '_blank')}
                                        className="p-0.5 text-brand-neutral hover:text-brand-primary transition-colors"
                                        title="Open in Zuper"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => { navigator.clipboard?.writeText(String(log.work_order_number)); setCopiedWo(log.id); setTimeout(() => setCopiedWo(null), 1200); }}
                                      className="p-0.5 text-brand-neutral hover:text-brand-primary transition-colors"
                                      title="Copy work order"
                                    >
                                      {copiedWo === log.id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                                    </button>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-brand-dark">{log.customer_name || '—'}</p>
                              {log.customer_phone && (
                                <p className="text-xs text-brand-neutral">{log.customer_phone}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {log.asset_name ? (
                                <>
                                  <p className="text-brand-dark text-xs font-medium">{log.asset_name}</p>
                                  {log.asset_serial_number && (
                                    <p className="text-[11px] text-brand-neutral">{log.asset_serial_number}</p>
                                  )}
                                </>
                              ) : <span className="text-brand-neutral">—</span>}
                            </td>
                            <td className="px-4 py-3 text-brand-dark text-xs">{formatDate(log.scheduled_date)}</td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-brand-dark">{log.scheduled_time || '—'}</span>
                            </td>
                            <td className="px-4 py-3">
                              {log.assigned_mechanic_name ? (
                                <span className="text-xs text-brand-dark flex items-center gap-1">
                                  <Wrench className="w-3 h-3 text-brand-neutral" />
                                  {log.assigned_mechanic_name}
                                </span>
                              ) : <span className="text-brand-neutral text-xs">—</span>}
                            </td>
                            <td className="px-4 py-3">
                              <BookedByBadge booked={booked} />
                            </td>
                            <td className="px-4 py-3 text-xs text-brand-neutral whitespace-nowrap">{formatDateTime(log.scheduled_at)}</td>
                            <td className="px-4 py-3">
                              {log.job_uid && (
                                <a
                                  href={`https://web.zuperpro.com/jobs/${log.job_uid}/details`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-neutral hover:text-brand-primary transition-colors"
                                  title="View in Zuper"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-brand-light/40">
                  {logs.map((log) => {
                    const booked = parseBookedBy(log.scheduled_by, staffNames);
                    return (
                      <div key={log.id} className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-brand-dark text-sm">{log.customer_name || '—'}</p>
                            <p className="text-xs text-brand-neutral font-mono">{log.work_order_number || '—'}</p>
                          </div>
                          <BookedByBadge booked={booked} />
                        </div>
                        {log.asset_name && (
                          <p className="text-xs text-brand-dark">{log.asset_name}{log.asset_serial_number ? ` · ${log.asset_serial_number}` : ''}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-brand-neutral">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(log.scheduled_date)}</span>
                          {log.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{log.scheduled_time}</span>}
                          {log.assigned_mechanic_name && <span className="flex items-center gap-1"><Wrench className="w-3 h-3" />{log.assigned_mechanic_name}</span>}
                        </div>
                        <p className="text-[11px] text-brand-neutral/70">Booked {formatDateTime(log.scheduled_at)}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-brand-neutral">
                Showing {page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-xl border border-brand-light hover:bg-brand-light/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-brand-dark" />
                </button>
                <span className="text-sm text-brand-dark font-medium px-2">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-xl border border-brand-light hover:bg-brand-light/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-brand-dark" />
                </button>
              </div>
            </div>
          )}

          {/* Booking Activity (confirmed, rescheduled, cancelled) */}
          {activity.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden mt-4">
              <div className="px-4 py-3 border-b border-brand-light bg-gradient-to-r from-brand-light/30 to-white">
                <h3 className="text-sm font-bold text-brand-dark">Booking Activity ({activity.length})</h3>
              </div>
              <div className="divide-y divide-brand-light/50">
                {activity.map((a) => (
                  <div key={a.id} className="px-4 py-3 flex items-center justify-between hover:bg-brand-light/10">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          a.action === 'confirmed' ? 'bg-green-100 text-green-700' :
                          a.action === 'rescheduled' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {a.action}
                        </span>
                        <span className="text-xs font-medium text-brand-dark">{a.customer_name}</span>
                        {a.work_order_number && <span className="text-[11px] font-mono text-brand-neutral">#{a.work_order_number}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-3 text-[11px] text-brand-neutral">
                        {a.scheduled_date && <span>Date: {a.scheduled_date}</span>}
                        {a.slot_label && <span>Slot: {a.slot_label}</span>}
                        {a.mechanic_name && <span>Tech: {a.mechanic_name}</span>}
                        {a.booked_by && <span>By: {parseBookedBy(a.booked_by, staffNames).label}</span>}
                        {a.details && <span className="text-brand-neutral/70">{a.details}</span>}
                      </div>
                    </div>
                    <div className="text-[11px] text-brand-neutral whitespace-nowrap ml-4">
                      {a.created_at ? new Date(String(a.created_at).endsWith('Z') ? String(a.created_at) : String(a.created_at).replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function BookedByBadge({ booked }) {
  if (booked.type === 'customer') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
        <User className="w-3 h-3" /> Customer
      </span>
    );
  }
  if (booked.type === 'staff') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-brand-light text-brand-primary border border-brand-light" title={booked.email}>
        <Users className="w-3 h-3" />
        <span className="max-w-[140px] truncate">{booked.label}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-100">
      {booked.label}
    </span>
  );
}
